// src/components/clients/dependants/DependantsList.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Users, UserPlus, Eye, Edit2, Trash2, Loader2 } from 'lucide-react';
import { clientService } from '../../../services/clientService';
//import DependantDetailsModal from './DependantDetailsModal';
import { DeleteModal } from '../../shared/DeleteModal';
import { ErrorAlert } from '../../shared/ErrorAlert';
import { FormatUtils } from '../../shared/FormatUtils';

const DependantsList = ({ clientId, isRetailClient }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [dependants, setDependants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedDependant, setSelectedDependant] = useState(null);
    const [deleteModal, setDeleteModal] = useState({ open: false, dependant: null });
    const [actionLoading, setActionLoading] = useState(false);

    useEffect(() => {
        if (clientId && isRetailClient) {
            fetchDependants();
        } else {
            setLoading(false);
        }
    }, [clientId, isRetailClient]);

    // Check if we're returning from the form page and should refresh
    useEffect(() => {
        if (location.state?.refreshDependants) {
            fetchDependants();
            // Clear the state
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    const fetchDependants = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await clientService.dependants.getByClientId(clientId);

            console.log('Dependants API response:', response); // ✅ Debug log

            // ✅ FIXED: Handle both wrapped and unwrapped responses
            let dependantsData = [];

            if (response.success && response.data) {
                // Response is wrapped: { success: true, data: [...] }
                dependantsData = response.data;
            } else if (Array.isArray(response.data)) {
                // Response has data property but no success: { data: [...] }
                dependantsData = response.data;
            } else if (Array.isArray(response)) {
                // Response is direct array: [...]
                dependantsData = response;
            }

            console.log('Processed dependants:', dependantsData); // ✅ Debug log
            setDependants(dependantsData);

        } catch (err) {
            console.error('Error fetching dependants:', err);
            setError(err.message || 'Failed to load dependants');
            setDependants([]);
        } finally {
            setLoading(false);
        }
    };

    const handleAddDependant = () => {
        navigate(`/clients/${clientId}/dependants/new`);
    };

    const handleEditDependant = (dependant) => {
        navigate(`/clients/${clientId}/dependants/${dependant.id}/edit`);
    };

    const handleDeleteDependant = async () => {
        if (!deleteModal.dependant) return;

        try {
            setActionLoading(true);
            const response = await clientService.dependants.delete(deleteModal.dependant.id);

            if (response.success) {
                await fetchDependants();
                setDeleteModal({ open: false, dependant: null });
            } else {
                throw new Error(response.message || 'Failed to delete dependant');
            }
        } catch (err) {
            console.error('Error deleting dependant:', err);
            setError(err.message || 'Failed to delete dependant');
        } finally {
            setActionLoading(false);
        }
    };

    const handleViewDetails = (dependant) => {
        setSelectedDependant(dependant);
    };

    const calculateAge = (dateOfBirth) => {
        if (!dateOfBirth) return null;
        const today = new Date();
        const birthDate = new Date(dateOfBirth);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    };

    // Don't show for corporate clients
    if (!isRetailClient) {
        return null;
    }

    if (loading) {
        return (
            <div className="text-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-2" />
                <p className="text-gray-500">Loading dependants...</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Dependants ({dependants.length})
                </h4>
                <button
                    onClick={handleAddDependant}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
                >
                    <UserPlus className="w-4 h-4" />
                    Add Dependant
                </button>
            </div>

            {/* Error Alert */}
            {error && <ErrorAlert error={error} onRetry={fetchDependants} />}

            {/* Dependants Table */}
            {dependants.length > 0 ? (
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Name
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Relationship
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Age
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Gender
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Date of Birth
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                            </th>
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                        {dependants.map((dependant) => (
                            <tr key={dependant.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-medium text-gray-900">
                                        {FormatUtils.formatName({
                                            firstName: dependant.firstName,
                                            middleName: dependant.middleName,
                                            lastName: dependant.lastName
                                        })}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                                        {dependant.relationship?.replace('_', ' ')}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">
                                        {dependant.age || calculateAge(dependant.dateOfBirth) || '-'} years
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">
                                        {dependant.gender?.replace('_', ' ') || '-'}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-600">
                                        {FormatUtils.formatDate(dependant.dateOfBirth, 'short')}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-2 py-1 text-xs rounded-full ${
                                        dependant.isActive
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-gray-100 text-gray-800'
                                    }`}>
                                        {dependant.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                    {dependant.isStudent && (
                                        <span className="ml-2 px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full">
                                            Student
                                        </span>
                                    )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                    <div className="flex items-center justify-center gap-2">
                                        <button
                                            onClick={() => handleViewDetails(dependant)}
                                            className="text-gray-600 hover:text-blue-600 transition"
                                            title="View Details"
                                        >
                                            <Eye className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => handleEditDependant(dependant)}
                                            className="text-gray-600 hover:text-indigo-600 transition"
                                            title="Edit"
                                        >
                                            <Edit2 className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => setDeleteModal({ open: true, dependant })}
                                            className="text-gray-600 hover:text-red-600 transition"
                                            title="Delete"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 mb-2">No dependants found</p>
                    <p className="text-sm text-gray-400 mb-4">Add dependants to track family members</p>
                    <button
                        onClick={handleAddDependant}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition inline-flex items-center gap-2"
                    >
                        <UserPlus className="w-4 h-4" />
                        Add First Dependant
                    </button>
                </div>
            )}

            {/* Details Modal - keeping this as a modal for quick viewing */}
            {selectedDependant && (
                <DependantDetailsModal
                    dependant={selectedDependant}
                    onClose={() => setSelectedDependant(null)}
                    onEdit={() => {
                        setSelectedDependant(null);
                        handleEditDependant(selectedDependant);
                    }}
                    onDelete={(dep) => {
                        setSelectedDependant(null);
                        setDeleteModal({ open: true, dependant: dep });
                    }}
                />
            )}

            {/* Delete Confirmation Modal */}
            <DeleteModal
                isOpen={deleteModal.open}
                onClose={() => setDeleteModal({ open: false, dependant: null })}
                onConfirm={handleDeleteDependant}
                itemName={deleteModal.dependant ?
                    FormatUtils.formatName({
                        firstName: deleteModal.dependant.firstName,
                        middleName: deleteModal.dependant.middleName,
                        lastName: deleteModal.dependant.lastName
                    }) : ''}
                itemType="dependant"
                loading={actionLoading}
            />
        </div>
    );
};

export default DependantsList;
